"use client"

import {
  Building2,
  ChevronLeft,
  CreditCard,
  FileSliders,
  FileText,
  KeyRound,
  Settings,
  Users,
  UserPen,
  UsersRound,
  Wrench,
} from "lucide-react";
import {useParams, usePathname} from "next/navigation";
import {useMemo} from "react";
import {NavAgent, NavAgentEntry} from "@/app/components/NavAgent";
import {usePreferredCompany} from "@/app/components/settings/usePreferredCompany";
import {companyPath, profileBillingPath, profilePersonalPath, profilePreferencesPath, settingsOrganizationBillingPath, settingsOrganizationSettingsPath, settingsOrganizationWorkspacesPath, settingsWorkspaceApiKeysPath, settingsWorkspaceGeneralPath, settingsWorkspaceIntegrationTabPath, settingsWorkspaceIntegrationsPath, settingsWorkspaceMembersPath, settingsWorkspaceReportingPath, settingsWorkspaceTeamsPath} from "@/app/paths";
import {useOwnCompanies, usePersonalSubscription, useUserPlans} from "@/hooks/queries/use-users";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useShallow} from "zustand/react/shallow";
import {isCompanyFeatureFlagEnabled} from "@/hooks/queries/feature-flags-utils";
import {useOptionalCompanyId} from "@/hooks/use-company-id";
import {subject} from "@casl/ability";
import {useCanCompany} from "@/app/components/Can";

function pathWithCheck(url: string) {
  return {
    url,
    activeCheck: (path: string) => path === url || path.startsWith(`${url}/`)
  }
}

export function SettingsSidebarNav() {
  const pathname = usePathname()
  const {orgId} = useParams<{ orgId?: string }>()
  const routeCompanyId = useOptionalCompanyId()
  const {preferredCompanyId} = usePreferredCompany()
  const userCompanies = useOwnCompanies()
  const {plans} = useUserPlans()
  const {subscription: personalSubscription} = usePersonalSubscription()
  const {ownLimits} = useBoundStore()
  const ownOrgs = useBoundStore(useShallow(s => {
    const orgIds = s.ownOrgDetails.map(d => d.orgId).sort()
    return orgIds.map(id => s.organizations[id]).filter(org => org !== undefined)
  }))

  const hasPersonalPlan = ownLimits !== undefined &&
    (plans?.find(p => p.tierId === ownLimits.currentTierId)?.pricing?.monthly?.priceCents ?? 0) > 0
  const hasActivePersonalPaidSubscription = hasPersonalPlan && personalSubscription?.status === "active"
  const showBilling = ownLimits !== undefined && hasActivePersonalPaidSubscription
  const showApiKeys = useBoundStore(s => isCompanyFeatureFlagEnabled(
    s.featureFlags,
    routeCompanyId ?? preferredCompanyId,
    ff => ff.wsApiKeys,
  ))
  const showReporting = useBoundStore(s => {
    const companyId = routeCompanyId ?? preferredCompanyId
    const companyLimits = companyId === undefined ? undefined : s.ownCompanyLimits[companyId]?.limits
    const paidOrTrialOrgEligible = Boolean(companyLimits?.paid && !companyLimits.forcedReadonly)
    return isCompanyFeatureFlagEnabled(
      s.featureFlags,
      companyId,
      ff => ff.wsReporting,
      paidOrTrialOrgEligible,
    )
  })
  const showIntegrationsV2 = useBoundStore(s => isCompanyFeatureFlagEnabled(
    s.featureFlags,
    routeCompanyId ?? preferredCompanyId,
    ff => ff.wsIntegrationsV2,
  ))
  const teamsFlagEnabled = useBoundStore(s => isCompanyFeatureFlagEnabled(
    s.featureFlags,
    routeCompanyId ?? preferredCompanyId,
    ff => ff.wsTeams,
  ))
  const currentOrgId = orgId ?? ownOrgs[0]?.id
  const workspaceNavCompanyId = routeCompanyId ?? preferredCompanyId
  const teamPermission = useCanCompany({
    do: "read",
    on: subject("Team", {companyId: workspaceNavCompanyId ?? 0}),
  })
  const showTeams = teamsFlagEnabled && teamPermission !== undefined && !teamPermission.forbidden

  const items = useMemo<NavAgentEntry[]>(() => {
    const settingsItems: NavAgentEntry[] = [{
      title: "Back to app",
      icon: ChevronLeft,
      ...pathWithCheck(preferredCompanyId ? companyPath(preferredCompanyId) : "/"),
      activeCheck: () => false,
    }]

    settingsItems.push({
      title: "Profile",
      items: [
        {
          title: "Personal Info",
          icon: UserPen,
          ...pathWithCheck(profilePersonalPath()),
        },
        {
          title: "Preferences",
          icon: FileSliders,
          ...pathWithCheck(profilePreferencesPath()),
        },
        ...(showBilling ? [{
          title: "Billing",
          icon: CreditCard,
          ...pathWithCheck(profileBillingPath()),
        }] : []),
      ],
    })

    if (workspaceNavCompanyId) {
      settingsItems.push({
        title: "Workspace",
        items: [
          {
            title: "General",
            icon: Settings,
            ...pathWithCheck(settingsWorkspaceGeneralPath(workspaceNavCompanyId)),
          },
          {
            title: "Members",
            icon: Users,
            ...pathWithCheck(settingsWorkspaceMembersPath(workspaceNavCompanyId)),
          },
          ...(showTeams ? [{
            title: "Teams",
            icon: UsersRound,
            ...pathWithCheck(settingsWorkspaceTeamsPath(workspaceNavCompanyId)),
          }] : []),
          ...(showReporting ? [{
            title: "Digests",
            icon: FileText,
            ...pathWithCheck(settingsWorkspaceReportingPath(workspaceNavCompanyId)),
          }] : []),
          ...(!showIntegrationsV2 ? [{
            title: "Integrations",
            icon: Wrench,
            url: settingsWorkspaceIntegrationTabPath(workspaceNavCompanyId, "code"),
            activeCheck: pathWithCheck(settingsWorkspaceIntegrationsPath(workspaceNavCompanyId)).activeCheck,
          }] : []),
          ...(showApiKeys ? [{
            title: "API Keys",
            icon: KeyRound,
            ...pathWithCheck(settingsWorkspaceApiKeysPath(workspaceNavCompanyId)),
          }] : []),
        ],
      })
    }

    if (currentOrgId) {
      settingsItems.push({
        title: "Organization",
        items: [
          {
            title: "General",
            icon: Settings,
            ...pathWithCheck(settingsOrganizationSettingsPath(currentOrgId)),
          },
          {
            title: "Workspaces",
            icon: Building2,
            ...pathWithCheck(settingsOrganizationWorkspacesPath(currentOrgId)),
          },
          {
            title: "Billing",
            icon: CreditCard,
            ...pathWithCheck(settingsOrganizationBillingPath(currentOrgId)),
          },
        ],
      })
    }

    return settingsItems
  }, [currentOrgId, preferredCompanyId, showApiKeys, showBilling, showIntegrationsV2, showReporting, showTeams, workspaceNavCompanyId])

  if (!pathname.startsWith("/settings")) {
    return null
  }

  return <NavAgent items={items}/>
}
